import { useQuery } from '@tanstack/react-query';
import { useWorkspaceScope } from '@/contexts/useWorkspaceScope';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { computeKeepWarm, type KeepWarmResult } from '@/lib/keepWarmEngine';

/** Raw row returned by get_keep_warm_queue RPC */
export interface KeepWarmQueueRow {
  lead_id: string;
  lead_name: string;
  phone: string | null;
  last_contacted_at: string | null;
  last_message_at: string | null;
  last_interaction_at: string | null;
  created_at: string;
  pipeline_stage: string | null;
  archived: boolean | null;
}

/** Lead + computed Keep Warm state for the page */
export interface KeepWarmQueueItem {
  lead: {
    id: string;
    name: string;
    phone: string | null;
    last_contacted_at: string | null;
    last_message_at: string | null;
    last_interaction_at: string | null;
    created_at: string;
    pipeline_stage: string | null;
    archived: boolean | null;
  };
  keepWarm: KeepWarmResult;
}

/**
 * Hook to get Keep Warm Queue: leads with at least one outbound contact,
 * filtered by eligibility and sorted: due_now first (by overdue desc), then cooling (by dueAt asc).
 */
export function useKeepWarmQueue(_cooldownHours: number = 48) {
  const { activeWorkspaceId } = useWorkspaceScope();
  const { user } = useAuth();

  return useQuery({
    queryKey: ['keep-warm-queue', activeWorkspaceId, user?.id],
    queryFn: async (): Promise<KeepWarmQueueItem[]> => {
      if (!activeWorkspaceId) throw new Error('No workspace');

      const { data: rows, error } = await supabase.rpc('get_keep_warm_queue', {
        p_workspace_id: activeWorkspaceId,
        p_user_id: user?.id || null,
        p_cooldown_hours: _cooldownHours,
      });

      if (error) {
        logger.error('[useKeepWarmQueue] Error fetching queue', error);
        return [];
      }

      const raw = (rows || []) as KeepWarmQueueRow[];
      const now = new Date();

      const leadInput = (r: KeepWarmQueueRow) => ({
        last_contacted_at: r.last_contacted_at,
        last_message_at: r.last_message_at,
        last_interaction_at: r.last_interaction_at,
        created_at: r.created_at,
        pipeline_stage: r.pipeline_stage,
        archived: r.archived,
      });

      const items: KeepWarmQueueItem[] = [];
      for (const r of raw) {
        const keepWarm = computeKeepWarm(leadInput(r), now);
        if (!keepWarm.isEligible) continue;
        items.push({
          lead: {
            id: r.lead_id,
            name: r.lead_name,
            phone: r.phone,
            last_contacted_at: r.last_contacted_at,
            last_message_at: r.last_message_at,
            last_interaction_at: r.last_interaction_at,
            created_at: r.created_at,
            pipeline_stage: r.pipeline_stage,
            archived: r.archived,
          },
          keepWarm,
        });
      }

      // Sort: due_now first by (now - dueAt) desc, then cooling by dueAt asc
      items.sort((a, b) => {
        const sa = a.keepWarm.state;
        const sb = b.keepWarm.state;
        if (sa === 'due_now' && sb !== 'due_now') return -1;
        if (sa !== 'due_now' && sb === 'due_now') return 1;
        if (sa === 'due_now' && sb === 'due_now') {
          const dueA = a.keepWarm.dueAt?.getTime() ?? 0;
          const dueB = b.keepWarm.dueAt?.getTime() ?? 0;
          return dueA - dueB; // oldest due first (smallest dueAt first)
        }
        // both cooling: sort by dueAt asc (soonest first)
        const dueA = a.keepWarm.dueAt?.getTime() ?? Infinity;
        const dueB = b.keepWarm.dueAt?.getTime() ?? Infinity;
        return dueA - dueB;
      });

      return items;
    },
    enabled: !!activeWorkspaceId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
